import { useRef, useReducer, useMemo } from 'react';
import { useDebounceFn } from './hooks/useDebounceFn.js';
import { GETKEY } from './hooks/useGetDataIndexColumns.js';
import { useIsomorphicLayoutEffect } from './hooks/useIsomorphicLayoutEffect.js';
import { useLatest } from './hooks/useLatest.js';
import { useLocalColumns } from './hooks/useLocalColumns.js';
import { useMemoizedFn } from './hooks/useMemoizedFn.js';
import { useSafeState } from './hooks/useSafeState.js';
import { useUpdateEffect } from './hooks/useUpdateEffect.js';
import ResizableHeader from './ResizableHeader.js';
import { depthFirstSearchWidthSet, isColHidden, isEmpty, isReservedColumn, isString, isUndefined } from './utils/index.js';
import { DefaultWidth } from './utils/constant.js';
import { validateColumns } from './utils/validateOptions.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
function useAntdResizableHeader(props) {
  const {
    columns: columnsProp,
    defaultWidth = DefaultWidth,
    minConstraints = defaultWidth / 2,
    maxConstraints = Number.POSITIVE_INFINITY,
    columnsState,
    onResizeStart: onResizeStartProp,
    onResizeEnd: onResizeEndProp,
    debounceWaitTime = 1e3
  } = props;
  validateColumns(columnsProp);
  const widthCache = useRef(/* @__PURE__ */ new Map());
  const [resizableColumns, setResizableColumns] = useSafeState(columnsProp || []);
  const latestColumns = useLatest(resizableColumns);
  const { localColumns, resetLocalColumns } = useLocalColumns({
    columnsState,
    columns: columnsProp,
    resizableColumns
  });
  const [tableWidth, setTableWidth] = useSafeState();
  const [shouldRender, forceRender] = useReducer((s) => s + 1, 0);
  const resetColumns = useMemoizedFn((resetStorage = true) => {
    widthCache.current.clear();
    resetLocalColumns(resetStorage);
  });
  const _onResizeEnd = useMemoizedFn((id) => (width) => {
    if (width) {
      const kvMap = /* @__PURE__ */ new Map();
      setResizableColumns((t) => {
        const nextColumns = depthFirstSearchWidthSet(
          t,
          (col) => col[GETKEY] === id && !!col.width && !isColHidden(col),
          width
        );
        function dig(cols) {
          var _a;
          for (let i = 0; i < cols.length; i++) {
            const col = cols[i];
            if (isColHidden(col)) continue;
            const key = col[GETKEY];
            kvMap.set((_a = String(key)) != null ? _a : "", { width: col == null ? void 0 : col.width, index: i });
            if (col == null ? void 0 : col.children) {
              dig(col.children);
            }
          }
        }
        dig(nextColumns);
        widthCache.current = kvMap;
        return nextColumns;
      });
    }
  });
  const onResizeStart = useMemoizedFn((col) => (width) => {
    onResizeStartProp == null ? void 0 : onResizeStartProp(__spreadProps(__spreadValues({}, col), {
      width,
      resizableColumns: latestColumns.current
    }));
  });
  const onResizeEnd = useMemoizedFn((col) => (width) => {
    onResizeEndProp == null ? void 0 : onResizeEndProp(__spreadProps(__spreadValues({}, col), {
      width,
      resizableColumns: latestColumns.current
    }));
    forceRender();
  });
  const getColumns = useMemoizedFn((list) => {
    const trulyColumns = list == null ? void 0 : list.filter((item) => !isEmpty(item) || isReservedColumn(item));
    const c = trulyColumns.map((col) => {
      var _a, _b, _c, _d;
      if (isReservedColumn(col)) {
        return col;
      }
      return __spreadProps(__spreadValues({}, col), {
        children: ((_a = col == null ? void 0 : col.children) == null ? void 0 : _a.length) ? getColumns(col.children) : void 0,
        onHeaderCell: (column) => {
          var _a2, _b2, _c2, _d2;
          return __spreadProps(__spreadValues({}, (_a2 = col.onHeaderCell) == null ? void 0 : _a2.call(col, column)), {
            "data-index": column.dataIndex,
            "title": isString(column == null ? void 0 : column.title) ? column == null ? void 0 : column.title : "",
            "width": ((_d2 = (_c2 = widthCache.current) == null ? void 0 : _c2.get((_b2 = column[GETKEY]) != null ? _b2 : "")) == null ? void 0 : _d2.width) || (column == null ? void 0 : column.width),
            "resizable": column.resizable,
            "_onResizeEnd": _onResizeEnd(column == null ? void 0 : column[GETKEY]),
            "onResizeStart": onResizeStart(column),
            "onResizeEnd": onResizeEnd(column),
            "minWidth": column.minConstraints || minConstraints,
            "maxWidth": column.maxConstraints || maxConstraints,
            "hide": isColHidden(column),
            shouldRender
          });
        },
        // ellipsis为true时，表格布局将变成 tableLayout="fixed"
        ellipsis: isUndefined(col.ellipsis) ? true : col.ellipsis,
        width: ((_d = (_c = widthCache.current) == null ? void 0 : _c.get((_b = col[GETKEY]) != null ? _b : "")) == null ? void 0 : _d.width) || (col == null ? void 0 : col.width),
        [GETKEY]: col[GETKEY] || col.key
      });
    });
    return c;
  });
  useIsomorphicLayoutEffect(() => {
    if (localColumns) {
      const c = getColumns(localColumns);
      setResizableColumns(c);
    }
  }, [localColumns]);
  useUpdateEffect(() => {
    const t = getColumns(resizableColumns);
    setResizableColumns(t);
  }, [shouldRender]);
  useIsomorphicLayoutEffect(() => {
    let width = 0;
    (function loop(cls) {
      for (let i = 0; i < cls.length; i++) {
        if (cls[i].children) {
          loop(cls[i].children);
        } else if (!isColHidden(cls[i])) {
          width += Number(cls[i].width) || cls[i].defaultWidth || defaultWidth;
        }
      }
    })(resizableColumns);
    setTableWidth(width);
  }, [defaultWidth, resizableColumns]);
  const { run: debounceRender } = useDebounceFn(forceRender, {
    wait: debounceWaitTime
  });
  useIsomorphicLayoutEffect(() => {
    window.addEventListener("resize", debounceRender);
    return () => {
      window.removeEventListener("resize", debounceRender);
    };
  }, []);
  const components = useMemo(
    () => ({
      header: {
        cell: ResizableHeader
      }
    }),
    []
  );
  return {
    resizableColumns,
    components,
    tableWidth,
    resetColumns,
    refresh: forceRender
  };
}

export { useAntdResizableHeader };
